import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { API } from "@/api";
import usePageMeta from "@/hooks/usePageMeta";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AdminLogin() {
  usePageMeta("Admin Sign In | Modify", "Modify website admin sign in.");
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("modify_admin_token", data.token);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="mx-auto flex max-w-md flex-col px-4 pb-24 pt-44 sm:px-6 lg:pt-40">
      <div className="rounded-3xl border border-line bg-white p-8 shadow-lg">
        <img src="/images/logo-dark.png" alt="Modify — We Care" data-testid="admin-login-logo" className="mb-6 w-40" />
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-sage-dark">Modify admin</p>
        <h1 className="mt-2 font-display text-3xl font-extrabold text-charcoal">Sign in</h1>
        <p className="mt-2 text-sm text-ink/60">Manage enquiries, gallery images and site content.</p>

        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="admin-login-form">
          <div>
            <label htmlFor="admin-email" className="mb-2 block text-xs font-bold uppercase tracking-[0.15em] text-ink/70">
              Email
            </label>
            <input
              id="admin-email"
              data-testid="admin-email-input"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="min-h-[48px] w-full rounded-xl border border-line bg-ivory px-4 py-3 text-base text-ink transition-colors duration-200 focus:border-sage focus:outline-none focus:ring-2 focus:ring-sage/30"
            />
          </div>
          <div>
            <label htmlFor="admin-password" className="mb-2 block text-xs font-bold uppercase tracking-[0.15em] text-ink/70">
              Password
            </label>
            <input
              id="admin-password"
              data-testid="admin-password-input"
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="min-h-[48px] w-full rounded-xl border border-line bg-ivory px-4 py-3 text-base text-ink transition-colors duration-200 focus:border-sage focus:outline-none focus:ring-2 focus:ring-sage/30"
            />
          </div>

          {error && (
            <p data-testid="admin-login-error" role="alert" className="rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="flex min-h-[52px] w-full items-center justify-center gap-2 rounded-full bg-charcoal px-7 py-3.5 text-sm font-bold tracking-[0.08em] text-white transition-colors duration-300 hover:bg-ink disabled:opacity-60"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            {loading ? "SIGNING IN..." : "SIGN IN"}
          </button>
        </form>
      </div>
    </div>
  );
}
